import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PeopleAltRounded as PeopleIcon,
  AdminPanelSettingsRounded as ShieldIcon,
  VpnKeyRounded as KeyIcon,
  ScheduleRounded as ScheduleIcon,
  TuneRounded as SettingsIcon,
  AccountTreeRounded as OrgChartIcon,
  AssignmentTurnedInRounded as ProjectIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  PersonOutlineRounded as UserViewIcon,
  VerifiedUserRounded as AdminBadgeIcon,
  AssessmentRounded as ReportsIcon,
} from '@mui/icons-material';

import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { ADMIN_PROPOSAL_PROJECT } from 'Shared/Security/Application/Constants/PermissionsConstant';
import { PRIVATE, ORGANIGRAMA, PRIVATE_ADMIN_REPORTS } from 'Shared/Routing/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_LIST_USERS, PROPOSAL_PROJECT } from 'User/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_LIST_PERMISSIONGROUPS } from 'PermissionGroup/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_LIST_PERMISSIONS } from 'Permission/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_LIST_SCHEDULED_COMMANDS } from 'ScheduledCommand/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_EXTRA_CONFIG } from 'Extra/AfterSalesExtra/Application/Constants/PathsConstant';

import { MODERN_COLORS, MODERN_SHADOWS, MODERN_RADIUS } from 'UI/Application/Components/ModernLayout/tokens';

export default function AdminDashboardView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { checkPermission, getMeFromLocalStorage } = useAuthContext();
  const user = getMeFromLocalStorage() || {};

  const modules = [
    {
      category: 'Seguridad y Accesos',
      items: [
        {
          title: 'Gestión de Usuarios',
          description: 'Control de cuentas de empleados, altas, bajas, roles asignados y estado de accesos.',
          path: PRIVATE_ADMIN_LIST_USERS,
          icon: <PeopleIcon sx={{ fontSize: 28 }} />,
          tag: 'Cuentas',
          color: MODERN_COLORS.primary,
        },
        {
          title: 'Grupos de Permisos',
          description: 'Definición de perfiles corporativos y agrupación de permisos por rol o departamento.',
          path: PRIVATE_ADMIN_LIST_PERMISSIONGROUPS,
          icon: <ShieldIcon sx={{ fontSize: 28 }} />,
          tag: 'Roles',
          color: MODERN_COLORS.primaryDark,
        },
        {
          title: 'Permisos Granulares',
          description: 'Catálogo de permisos de seguridad y control de capacidades del sistema.',
          path: PRIVATE_ADMIN_LIST_PERMISSIONS,
          icon: <KeyIcon sx={{ fontSize: 28 }} />,
          tag: 'Seguridad',
          color: '#0891b2',
        },
      ],
    },
    {
      category: 'Operaciones y Sistema',
      items: [
        {
          title: 'Tareas Programadas',
          description: 'Supervisión de procesos automatizados, cron jobs y servicios de sincronización.',
          path: PRIVATE_ADMIN_LIST_SCHEDULED_COMMANDS,
          icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
          tag: 'Automatización',
          color: '#2563eb',
        },
        {
          title: 'Configuración Posventa',
          description: 'Reglas operativas, parámetros generales y tipos de extras de servicio técnico.',
          path: PRIVATE_ADMIN_EXTRA_CONFIG,
          icon: <SettingsIcon sx={{ fontSize: 28 }} />,
          tag: 'Posventa',
          color: '#0284c7',
        },
        {
          title: 'Informes Generados',
          description: 'Repositorio centralizado de reportes, organigramas y volcados Excel/PDF. Descarga y gestión de archivos.',
          path: PRIVATE_ADMIN_REPORTS,
          icon: <ReportsIcon sx={{ fontSize: 28 }} />,
          tag: 'Informes',
          color: '#059669',
        },
        ...(checkPermission(ADMIN_PROPOSAL_PROJECT)
          ? [
              {
                title: 'Propuestas de Proyectos',
                description: 'Revisión y supervisión de las propuestas de proyectos registradas.',
                path: PROPOSAL_PROJECT,
                icon: <ProjectIcon sx={{ fontSize: 28 }} />,
                tag: 'Proyectos',
                color: '#4f46e5',
              },
            ]
          : []),
      ],
    },
    {
      category: 'Estructura Organizacional',
      items: [
        {
          title: 'Organigrama de Empresa',
          description: 'Visualización y gestión de la estructura jerárquica y equipos de Aszende.',
          path: ORGANIGRAMA,
          icon: <OrgChartIcon sx={{ fontSize: 28 }} />,
          tag: 'Organización',
          color: '#0ea5e9',
        },
      ],
    },
  ];

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto', pb: 4 }}>
      {/* Hero Banner Superior */}
      <Card
        sx={{
          mb: 4,
          borderRadius: MODERN_RADIUS.card,
          background: `linear-gradient(135deg, ${MODERN_COLORS.bgCard} 0%, ${MODERN_COLORS.primaryIce} 100%)`,
          border: `1.5px solid ${MODERN_COLORS.borderSoft}`,
          boxShadow: MODERN_SHADOWS.card,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Fondo decorativo sutil */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${MODERN_COLORS.primaryLight}22 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 } }}>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip
                  icon={<AdminBadgeIcon sx={{ fontSize: '1rem !important', color: `${MODERN_COLORS.primaryDark} !important` }} />}
                  label="Panel de Administración"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    bgcolor: MODERN_COLORS.primaryIce2,
                    color: MODERN_COLORS.primaryDark,
                    border: `1px solid ${MODERN_COLORS.borderSoft}`,
                    py: 0.5,
                  }}
                />
                {user.email && (
                  <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, fontWeight: 500 }}>
                    Sesión: <strong>{user.email}</strong>
                  </Typography>
                )}
              </Stack>

              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 800,
                  color: MODERN_COLORS.textMain,
                  lineHeight: 1.2,
                  mb: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                Centro de Gestión y Control
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: MODERN_COLORS.textMuted,
                  maxWidth: 680,
                  lineHeight: 1.5,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Accede directamente a los módulos administrativos de la intranet. Gestiona permisos, usuarios del sistema y automatizaciones sin necesidad de buscar en menús ocultos.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                component={Link}
                to={PRIVATE}
                variant="outlined"
                startIcon={<UserViewIcon />}
                sx={{
                  borderRadius: MODERN_RADIUS.pill,
                  px: 2.5,
                  py: 1.2,
                  fontWeight: 700,
                  borderColor: MODERN_COLORS.borderCyan,
                  color: MODERN_COLORS.primaryDark,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(2, 132, 199, 0.08)',
                  '&:hover': {
                    borderColor: MODERN_COLORS.primary,
                    bgcolor: MODERN_COLORS.primaryIce,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Volver a Vista Usuario
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Secciones de Módulos */}
      {modules.map((section, sIndex) => (
        <Box key={sIndex} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: MODERN_COLORS.textMain,
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
              }}
            >
              {section.category}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                bgcolor: MODERN_COLORS.borderLight,
              }}
            />
          </Box>

          <Grid container spacing={2.5}>
            {section.items.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  component={Link}
                  to={item.path}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    textDecoration: 'none',
                    bgcolor: MODERN_COLORS.bgCard,
                    borderRadius: MODERN_RADIUS.card,
                    border: `1.5px solid ${MODERN_COLORS.borderLight}`,
                    boxShadow: MODERN_SHADOWS.card,
                    p: 2.5,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: MODERN_COLORS.borderCyan,
                      boxShadow: MODERN_SHADOWS.tableHover,
                      '& .action-arrow': {
                        transform: 'translateX(4px)',
                        color: MODERN_COLORS.primary,
                      },
                      '& .icon-wrapper': {
                        bgcolor: MODERN_COLORS.primary,
                        color: 'white',
                      },
                    },
                  }}
                >
                  {/* Fila superior: Icono + Tag */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box
                      className="icon-wrapper"
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: MODERN_COLORS.primaryIce2,
                        color: item.color || MODERN_COLORS.primaryDark,
                        transition: 'all 0.25s ease',
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Chip
                      label={item.tag}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.675rem',
                        bgcolor: MODERN_COLORS.bgHeader,
                        color: MODERN_COLORS.textMuted,
                        border: `1px solid ${MODERN_COLORS.borderLight}`,
                        borderRadius: MODERN_RADIUS.pill,
                      }}
                    />
                  </Stack>

                  {/* Contenido: Título + Descripción */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: MODERN_COLORS.textMain,
                      fontSize: '1.05rem',
                      lineHeight: 1.3,
                      mb: 1,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: MODERN_COLORS.textMuted,
                      lineHeight: 1.5,
                      fontSize: '0.85rem',
                      flex: 1,
                      mb: 2.5,
                    }}
                  >
                    {item.description}
                  </Typography>

                  {/* Footer de la tarjeta con acción */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      pt: 1.5,
                      borderTop: `1px solid ${MODERN_COLORS.borderLight}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: MODERN_COLORS.primaryDark,
                        fontSize: '0.8rem',
                      }}
                    >
                      Acceder al módulo
                    </Typography>

                    <ArrowForwardIcon
                      className="action-arrow"
                      sx={{
                        fontSize: 18,
                        color: MODERN_COLORS.primaryDark,
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
